import { X } from 'lucide-react';

import { useEffect, useMemo } from 'react';

const PDFPreviewModal = ({
	isOpen,
	onClose,
	documentContent,
	documentTitle,
	onDownload,
	onPrint
}) => {
	// Create an object URL for the PDF blob so we can embed it
	const pdfUrl = useMemo(
		() => (documentContent ? URL.createObjectURL(documentContent) : null),
		[documentContent]
	);

	// Cleanup when modal closes or blob changes
	useEffect(() => {
		return () => {
			if (pdfUrl) URL.revokeObjectURL(pdfUrl);
		};
	}, [pdfUrl]);

	if (!isOpen) return null;

	return (
		<>
			{/* Backdrop Overlay */}
			<div
				className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm transition-opacity duration-300"
				onClick={onClose}
			/>

			{/* Side Panel - Slides in from right */}
			<div
				className="fixed top-0 right-0 bottom-0 z-[9999] w-[600px] bg-white shadow-2xl transform transition-transform duration-300 ease-out flex flex-col"
				style={{ transform: isOpen ? 'translateX(0)' : 'translateX(100%)' }}
			>
				{/* Header */}
				<div className="flex items-center justify-between px-8 py-6 border-b border-gray-200">
					<div>
						<h2 className="text-2xl font-bold text-dark-blue">PDF preview</h2>
						<p className="text-sm text-gray-500 truncate max-w-[360px]">{documentTitle}</p>
					</div>

					<div className="flex items-center gap-3">
						<button
							onClick={onDownload}
							className="px-4 py-2 rounded-lg border border-gray-200 text-dark-blue hover:bg-gray-100 transition-colors"
							disabled={!pdfUrl}
						>
							Download
						</button>
						<button
							onClick={onPrint}
							className="px-4 py-2 rounded-lg border border-gray-200 text-dark-blue hover:bg-gray-100 transition-colors"
							disabled={!pdfUrl}
						>
							Print
						</button>
						<button
							onClick={onClose}
							className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-dark-blue hover:bg-gray-100 rounded-lg transition-colors"
						>
							<X className="w-5 h-5" />
							<span className="font-medium">Close</span>
						</button>
					</div>
				</div>

				{/* Document Content - Embedded PDF */}
				<div className="flex-1 overflow-hidden bg-gray-50 p-6">
					<div className="bg-white shadow-lg mx-auto w-full h-full rounded-lg overflow-hidden border border-gray-200">
						{pdfUrl ? (
							<iframe
								title="PDF Preview"
								src={pdfUrl}
								className="w-full h-full"
								style={{ minHeight: '100%' }}
							/>
						) : (
							<div className="flex items-center justify-center h-full text-gray-500">
								Generating preview...
							</div>
						)}
					</div>
				</div>
			</div>
		</>
	);
};

export default PDFPreviewModal;
